import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import AppDeploymentsService from '../services/appDeploymentsService';
import * as CardTemplatesServiceFunctions from '../services/cardTemplatesService';

// פונקציית עזר לדיבאונסינג
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const useAppDeployment = (hookFileId) => {
  const [deployStatus, setDeployStatus] = useState(null);
  const [cardTemplates, setCardTemplates] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const appDeploymentsService = new AppDeploymentsService();
  
  // Create a reference to the debounced save function
  const debouncedSaveRef = useRef(null);

  // Initialize the debounced save function
  useEffect(() => {
    debouncedSaveRef.current = debounce(async (deploymentId, substepId, data) => {
      if (!deploymentId) return;
      setIsSaving(true);
      try {
        const now = new Date().toISOString();
        await appDeploymentsService.updateDeployment(deploymentId, {
          updatedAt: now,
          lastInteractionAt: now,
          // Use a nested object to update just the specific substep data
          formData: {
            [substepId]: data
          }
        });
        
        setLastSavedAt(new Date());
        console.log(`Temporary data for ${substepId} saved to Firebase`);
      } catch (error) {
        console.error('Error saving temporary data to Firebase:', error);
      } finally {
        setIsSaving(false);
      }
    }, 800); // Debounce for 800ms
  }, [appDeploymentsService]);

  // פונקציה לטעינת תבניות הכרטיסים
  const loadCardTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      // Fetch templates from Firebase using the imported functions
      const templatesFromFirebase = await CardTemplatesServiceFunctions.getAllCardTemplates();
      
      if (!templatesFromFirebase || templatesFromFirebase.length === 0) {
        console.warn('No card templates found in Firebase or service failed.');
        setCardTemplates([]);
        setError('לא נמצאו תבניות כרטיסים ב-Firebase.');
        return [];
      }
      
      // Assuming the service returns templates sorted by 'order'
      setCardTemplates(templatesFromFirebase);
      console.log('Card templates loaded successfully from Firebase:', templatesFromFirebase.length, 'templates');
      return templatesFromFirebase;
    } catch (error) {
      console.error('Error loading card templates from Firebase:', error);
      setError('Failed to load card templates from Firebase');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // פונקציה להתחלת תהליך הדיפלוי
  const startDeployment = async (providedFileId = null) => {
    // השתמש ב-fileId מהקלוזור של ההוק או ב-providedFileId אם הועבר
    const fileId = providedFileId || hookFileId;
    
    console.log('Starting deployment for fileId:', fileId);
    
    if (!fileId) {
      console.error('No fileId provided to startDeployment');
      setError('נדרש מזהה קובץ להתחלת התהליך');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Load card templates first
      await loadCardTemplates();
      
      // Check if we already have a deployment for this file
      const existingDeployment = await appDeploymentsService.getDeploymentByFileId(fileId);
      
      let deploymentId;
      
      if (existingDeployment) {
        // Resume existing deployment
        deploymentId = existingDeployment.id;
        console.log('Resuming existing deployment with ID:', deploymentId);
        setDeployStatus(existingDeployment);
        setCurrentCardIndex(existingDeployment.currentCardIndex || 0);
      } else {
        // Start a new deployment
        console.log('Creating new deployment for fileId:', fileId);
        
        // מוודא שיש תבניות כרטיסים לפני שמתחילים דיפלוימנט
        if (!cardTemplates || cardTemplates.length === 0) {
          console.error('No card templates available, trying to reload');
          await loadCardTemplates();
          
          if (!cardTemplates || cardTemplates.length === 0) {
            console.error('Still no card templates after reload');
            throw new Error('לא ניתן ליצור דיפלוימנט - אין תבניות כרטיסים זמינות');
          }
        }
        
        const newDeployment = {
          fileId: fileId,
          currentCardIndex: 0,
          formData: {}, // מידע זמני של טפסים
          status: 'initialized',
          createdAt: new Date(),
          updatedAt: new Date(),
          startedAt: null 
        };
        
        try {
          deploymentId = await appDeploymentsService.createDeployment(newDeployment);
          console.log('Deployment created successfully with ID:', deploymentId);
        } catch (createError) {
          console.error('Error in createDeployment:', createError);
          console.error('Error stack:', createError.stack);
          throw createError;
        }
        
        setDeployStatus({ id: deploymentId, ...newDeployment });
      }
      
      setIsLoading(false);
      return deploymentId;
    } catch (error) {
      console.error('Error in startDeployment:', error);
      setError('אירעה שגיאה בעת התחלת התהליך: ' + (error.message || 'שגיאה לא ידועה'));
      setIsLoading(false);
      throw error;
    }
  };

  // פונקציה למעבר לכרטיס הבא + שמירת כל הנתונים בפיירבייס
  const goToNextCard = async () => {
    if (!deployStatus) return;
    
    try {
      setIsSaving(true);
      
      if (currentCardIndex < cardTemplates.length - 1) {
        const nextIndex = currentCardIndex + 1;
        const nextStage = cardTemplates[nextIndex]?.id || '';
        const now = new Date().toISOString();
        
        // מכין עדכון עם כל המידע: סטטוס + מידע טפסים זמני במקום אחד
        const update = {
          currentCardIndex: nextIndex,
          updatedAt: now,
          lastInteractionAt: now,
          formData: formData // שמירת כל מידע הטפסים הזמני
        };
        
        // אם זה שלב חדש ולא רק תת-שלב, עדכן גם את המצב הנוכחי
        // אבל רק אם השלב לא הושלם עדיין - אחרת נשאיר את currentStage כפי שהוא
        if (nextStage && 
            nextStage !== cardTemplates[currentCardIndex]?.id &&
            (!deployStatus.stages?.[nextStage] || deployStatus.stages[nextStage].status !== 'completed')) {
          
          // עדכן את currentStage רק אם השלב הבא לא הושלם
          update.currentStage = nextStage;
          
          // Only update stages if needed
          if (!deployStatus.stages?.[nextStage]) {
            // Only initialize stages if they don't exist yet
            update.stages = {
              ...deployStatus.stages,
              [nextStage]: {
                status: 'processing',
                startedAt: now
              }
            };
          } else if (deployStatus.stages[nextStage]?.status !== 'completed') {
            // Only change status if the stage isn't already completed
            update.stages = {
              ...deployStatus.stages,
              [nextStage]: {
                ...deployStatus.stages[nextStage],
                status: 'processing',
                startedAt: deployStatus.stages[nextStage]?.startedAt || now
              }
            };
          }
          
          // Only mark the previous stage as completed if it's not already completed
          const prevStage = cardTemplates[currentCardIndex]?.id;
          if (prevStage && 
              deployStatus.stages?.[prevStage] && 
              deployStatus.stages[prevStage].status !== 'completed') {
            if (!update.stages) {
              update.stages = { ...deployStatus.stages };
            }
            update.stages[prevStage] = {
              ...deployStatus.stages[prevStage],
              status: 'completed',
              completedAt: now
            };
          }
        }
        
        await appDeploymentsService.updateDeployment(deployStatus.id, update);
        setLastSavedAt(new Date());
        
        // עדכון המצב המקומי
        setCurrentCardIndex(nextIndex);
        
        return nextIndex; // Return the new index for immediate local state update
      }
    } catch (error) {
      console.error('Error navigating to next card:', error);
      setError('Failed to navigate to the next step. Please try again.');
      throw error; // Re-throw the error so it can be caught by the caller
    } finally {
      setIsSaving(false);
    }
  };

  // פונקציה למעבר לכרטיס הקודם
  const goToPrevCard = async () => {
    if (!deployStatus || currentCardIndex <= 0) return;
    
    try {
      setIsSaving(true);
      
      const prevIndex = currentCardIndex - 1;
      const now = new Date().toISOString();
      
      // Get previous stage ID
      const prevStage = cardTemplates[prevIndex]?.id || '';
      
      // עדכון הסטטוס בפיירבייס + שמירת מידע טפסים זמני
      const update = {
        currentCardIndex: prevIndex,
        updatedAt: now,
        lastInteractionAt: now,
        formData: formData // שמירת המידע הזמני
      };
      
      // מנטרלים לחלוטין את העדכון של currentStage בעת חזרה לכרטיסים קודמים
      // כך הסטטוס הכללי של התהליך לא ישתנה כשמשתמש רק צופה בכרטיסים שכבר הושלמו
      
      await appDeploymentsService.updateDeployment(deployStatus.id, update);
      
      setLastSavedAt(new Date());
      
      // עדכון המצב המקומי
      setCurrentCardIndex(prevIndex);
      
      return prevIndex; // Return the new index for immediate local state update
    } catch (error) {
      console.error('Error navigating to previous card:', error);
      setError('Failed to navigate to the previous step. Please try again.');
      throw error; // Re-throw the error so it can be caught by the caller
    } finally {
      setIsSaving(false);
    }
  };

  // עדכון מידע זמני בסטייט מקומי בלבד (לא נשמר באופן אוטומטי)
  const saveTemporaryData = useCallback((substepId, data) => {
    // שמירה בסטייט לוקאלי בלבד
    setFormData(prev => ({
      ...prev,
      [substepId]: data
    }));
    
    // Also save to Firebase with debouncing to avoid too many writes
    if (deployStatus?.id && debouncedSaveRef.current) {
      debouncedSaveRef.current(deployStatus.id, substepId, data);
    }
  }, [deployStatus?.id]);
  
  // פונקציה לשמירת תשובה סופית לשלב
  const saveAnswer = async (stageId, substepId, answer) => {
    try {
      setIsSaving(true);
      
      const now = new Date().toISOString();
      
      // עדכון התשובה ישירות + מידע זמני בפירבייס
      await appDeploymentsService.updateDeployment(deployStatus.id, {
        updatedAt: now,
        lastInteractionAt: now,
        formData: formData, // שומר את כל הנתונים הזמניים
        stages: {
          [stageId]: {
            answers: {
              [substepId]: {
                value: answer,
                answeredAt: now
              }
            },
            substepsProgress: {
              [substepId]: {
                status: "completed",
                completedAt: now
              }
            }
          }
        }
      });
      
      // מוחק את המידע הזמני מהסטייט המקומי
      setFormData(prev => {
        const newData = {...prev};
        delete newData[substepId];
        return newData;
      });
      
      setLastSavedAt(new Date());
      return true;
    } catch (error) {
      console.error('Error saving answer:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };
  
  // אפקט לטעינת תבניות הכרטיסים בעת אתחול הקומפוננטה
  useEffect(() => {
    loadCardTemplates();
  }, [loadCardTemplates]);

  // אפקט להאזנה לשינויים במצב התקדמות הדיפלוי
  useEffect(() => {
    if (!hookFileId) {
      setIsLoading(false);
      return;
    }

    console.log('Setting up Firebase listener for appDeployments document with ID:', hookFileId);
    setIsLoading(true);
    
    // בדיקה אם המסמך קיים ב-Firestore
    const checkDocument = async () => {
      try {
        const docRef = doc(db, 'appDeployments', hookFileId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          console.log(`Document with ID ${hookFileId} doesn't exist, trying to find by fileId`);
          
          const existingDeployment = await appDeploymentsService.getDeploymentByFileId(hookFileId);
          
          if (existingDeployment) {
            console.log('Found deployment by fileId:', existingDeployment);
            setDeployStatus(existingDeployment);
            setCurrentCardIndex(existingDeployment.currentCardIndex || 0);
            setFormData(existingDeployment.formData || {});
          }
        }
      } catch (error) {
        console.error('Error checking document:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    // הרשמה לשינויים במסמך
    let unsubscribe = () => {};
    
    try {
      unsubscribe = onSnapshot(
        doc(db, 'appDeployments', hookFileId),
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = { id: docSnapshot.id, ...docSnapshot.data() };
            console.log('Document updated in Firebase:', data);
            
            setDeployStatus(data);
            setCurrentCardIndex(data.currentCardIndex || 0);
            
            // עדכון מידע זמני מהפיירבייס אם קיים
            if (data.formData) {
              setFormData(data.formData);
            }
          } else {
            console.log(`Document ${hookFileId} doesn't exist in onSnapshot`);
            checkDocument();
          }
          
          setIsLoading(false);
        },
        (error) => {
          console.error('Error in onSnapshot:', error);
          setError('Failed to get updates from Firebase');
          setIsLoading(false);
        }
      );
    } catch (error) {
      console.error('Error setting up Firebase listener:', error);
      setIsLoading(false);
    }

    // בדיקה ראשונית
    checkDocument();
    
    return () => {
      unsubscribe();
    };
  }, [hookFileId]);

  return {
    deployStatus,
    cardTemplates,
    currentCardIndex,
    isLoading,
    error,
    formData,
    isSaving,
    lastSavedAt,
    startDeployment,
    goToNextCard,
    goToPrevCard,
    saveTemporaryData,
    saveAnswer
  };
};

export default useAppDeployment; 